import React, { useState, useEffect } from 'react';
import PageWrapper from '@/components/common/PageWrapper';
import HeaderSection from '@/components/HeaderSection';
import {
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  UserPlusIcon,
  ClockIcon,
  CalendarIcon,
  DocumentTextIcon,
  ChartBarSquareIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  Phone as PhoneMuiIcon,
  Email as EmailMuiIcon,
  Home as HomeMuiIcon,
  Business as BusinessMuiIcon,
  Videocam as VideocamMuiIcon,
} from '@mui/icons-material';
import { FaWhatsapp } from 'react-icons/fa';
import { FiPhone } from 'react-icons/fi';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchDashboard,
  selectMetrics,
  selectOverdueFollowups,
  selectFollowupsDueToday,
  selectTodayAppointments,
  selectLoading,
  selectError,
} from './dailySalesDashboardSlice';
import { useNavigate } from 'react-router-dom';
import AddLeadModal from '@/feature/leads/components/modules/Lead/AddLeadModal';

function DailySalesDashboard() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);

  const metrics = useAppSelector(selectMetrics);
  const overdueFollowups = useAppSelector(selectOverdueFollowups);
  const followupsDueToday = useAppSelector(selectFollowupsDueToday);
  const appointments = useAppSelector(selectTodayAppointments);
  const loading = useAppSelector(selectLoading);
  const error = useAppSelector(selectError);

  useEffect(() => {
    dispatch(fetchDashboard());
  }, [dispatch]);

  useEffect(() => {
    console.log('appointments', appointments)
  }, [appointments])

  // Helper function to open WhatsApp
  const openWhatsApp = (phone?: string, whatsapp?: string) => {
    const targetNumber = whatsapp || phone;
    if (!targetNumber) return;

    const cleaned = targetNumber.replace(/\D/g, '');
    if (!cleaned) return;

    const whatsappUrl = `https://wa.me/${cleaned}`;
    window.open(whatsappUrl, '_blank');
  };

  // Helper function to start phone call
  const startPhoneCall = (phone?: string) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  const getMetricCardStyles = (color: string) => {
    const styles = {
      blue: {
        bar: 'bg-blue-500',
        icon: 'text-blue-500',
        bg: 'bg-blue-50',
      },
      yellow: {
        bar: 'bg-yellow-500',
        icon: 'text-yellow-500',
        bg: 'bg-yellow-50',
      },
      'light-blue': {
        bar: 'bg-cyan-500',
        icon: 'text-cyan-500',
        bg: 'bg-cyan-50',
      },
      gray: {
        bar: 'bg-gray-400',
        icon: 'text-gray-500',
        bg: 'bg-gray-50',
      },
    };
    return styles[color as keyof typeof styles] || styles.gray;
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case 'site':
        return <HomeMuiIcon className="h-5 w-5" />;
      case 'showroom':
        return <BusinessMuiIcon className="h-5 w-5" />;
      case 'virtual':
        return <VideocamMuiIcon className="h-5 w-5" />;
      default:
        return <CalendarIcon className="h-5 w-5" />;
    }
  };

  if (loading && !metrics) {
    return (
      <PageWrapper>
        <div className="flex justify-center items-center min-h-[400px]">
          <CircularProgress />
        </div>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </PageWrapper>
    );
  }

  const appointmentsCount = appointments.length;
  const followupsCount = followupsDueToday.length + overdueFollowups.length;

  return (
    <PageWrapper>
      <HeaderSection
        title="Daily Sales Dashboard"
        description={`Welcome back, Marc. You have ${appointmentsCount} appointments and ${followupsCount} follow-ups today.`}
        icon={<ChartBarSquareIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />}
        buttonText="New Lead"
        buttonIcon={<PlusIcon className="h-4 w-4 mr-2" />}
        onButtonClick={() => setIsAddLeadModalOpen(true)}
      />
      <div className="max-w-[1400px] w-full mx-auto flex flex-col gap-6">
        {/* Key Metrics Cards */}
        {metrics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* New Leads */}
            <Card className="relative overflow-hidden" onClick={() => navigate('/leads')}>
              <div
                className={`absolute left-0 top-0 bottom-0 w-1 ${getMetricCardStyles(metrics.newLeads.color).bar}`}
              />
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      NEW LEADS
                    </p>
                    <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">
                      {metrics.newLeads.count}
                    </p>
                    <p className="mt-1 text-sm text-green-600">
                      {metrics.newLeads.change}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg ${getMetricCardStyles(metrics.newLeads.color).bg}`}>
                    <UserPlusIcon
                      className={`h-6 w-6 ${getMetricCardStyles(metrics.newLeads.color).icon}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Follow-ups Due */}
            <Card className="relative overflow-hidden">
              <div
                className={`absolute left-0 top-0 bottom-0 w-1 ${getMetricCardStyles(metrics.followUpsDue.color).bar}`}
              />
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      FOLLOW-UPS DUE
                    </p>
                    <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">
                      {metrics.followUpsDue.count}
                    </p>
                    <p className="mt-1 text-sm text-orange-600">
                      {metrics.followUpsDue.priority}
                    </p>
                  </div>
                  <div
                    className={`p-2 rounded-lg ${getMetricCardStyles(metrics.followUpsDue.color).bg}`}
                  >
                    <ClockIcon
                      className={`h-6 w-6 ${getMetricCardStyles(metrics.followUpsDue.color).icon}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Today's Appointments */}
            <Card className="relative overflow-hidden">
              <div
                className={`absolute left-0 top-0 bottom-0 w-1 ${getMetricCardStyles(metrics.todayApps.color).bar}`}
              />
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      TODAY'S APPS
                    </p>
                    <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">
                      {metrics.todayApps.count}
                    </p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                      {metrics.todayApps.firstTime}
                    </p>
                  </div>
                  <div
                    className={`p-2 rounded-lg ${getMetricCardStyles(metrics.todayApps.color).bg}`}
                  >
                    <CalendarIcon
                      className={`h-6 w-6 ${getMetricCardStyles(metrics.todayApps.color).icon}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quotes Pending */}
            <Card className="relative overflow-hidden">
              <div
                className={`absolute left-0 top-0 bottom-0 w-1 ${getMetricCardStyles(metrics.quotesPending.color).bar}`}
              />
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      QUOTES PENDING
                    </p>
                    <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">
                      {metrics.quotesPending.count}
                    </p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                      {metrics.quotesPending.value}
                    </p>
                  </div>
                  <div
                    className={`p-2 rounded-lg ${getMetricCardStyles(metrics.quotesPending.color).bg}`}
                  >
                    <DocumentTextIcon
                      className={`h-6 w-6 ${getMetricCardStyles(metrics.quotesPending.color).icon}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content - Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overdue Follow-ups */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4 bg-red-100">
                  <div className="p-1 bg-red-100 rounded">
                    <span className="text-red-600 font-bold text-lg">!</span>
                  </div>
                  <h3 className="text-lg font-semibold text-red-600">
                    Overdue Follow-ups
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <div className="max-h-[400px] overflow-y-auto">
                    <Table size="small">
                      <TableHead className="sticky top-0 bg-white dark:bg-slate-800 z-10">
                        <TableRow>
                          <TableCell className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                            LEAD NAME
                          </TableCell>
                          <TableCell className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                            INTEREST
                          </TableCell>
                          <TableCell className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                            LATE BY
                          </TableCell>
                          <TableCell className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                            ACTION
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                      {overdueFollowups.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            className="px-6 py-8 text-center text-slate-500"
                          >
                            No overdue follow-ups
                          </TableCell>
                        </TableRow>
                      ) : (
                        overdueFollowups.map((followup) => (
                          <TableRow
                            key={followup.id}
                            className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                          >
                            <TableCell className="px-6 py-4">
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-slate-900 dark:text-white">
                                  {followup.name}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {followup.contact}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                              {followup.interest}
                            </TableCell>
                            <TableCell className="px-6 py-4">
                              <Chip
                                label={followup.lateBy}
                                size="small"
                                className="bg-red-100 text-red-700 font-semibold"
                              />
                            </TableCell>
                            <TableCell className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="text"
                                  onClick={() => openWhatsApp(followup.phone, followup.whatsapp)}
                                  disabled={!followup.phone && !followup.whatsapp}
                                  sx={{
                                    color: '#25D366',
                                    minWidth: 'auto',
                                    padding: '4px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                    },
                                    '&:disabled': {
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  <FaWhatsapp size={20} />
                                </Button>
                                <Button
                                  variant="text"
                                  onClick={() => startPhoneCall(followup.phone)}
                                  disabled={!followup.phone}
                                  sx={{
                                    color: '#0284c7',
                                    minWidth: 'auto',
                                    padding: '4px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(2, 132, 199, 0.1)',
                                    },
                                    '&:disabled': {
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  <FiPhone size={20} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Follow-ups Due Today */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Follow-ups Due Today
                  </h3>
                  <Button size="small" variant="outlined" className="text-xs">
                    SORT BY: TIME
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <div className="max-h-[400px] overflow-y-auto">
                    <Table size="small">
                      <TableHead className="sticky top-0 bg-white dark:bg-slate-800 z-10">
                        <TableRow>
                          <TableCell className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                            TIME
                          </TableCell>
                          <TableCell className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                            LEAD NAME
                          </TableCell>
                          <TableCell className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                            CURRENT STAGE
                          </TableCell>
                          <TableCell className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                            DESCRIPTION
                          </TableCell>
                          <TableCell className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                            ACTION
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                      {followupsDueToday.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="px-6 py-8 text-center text-slate-500"
                          >
                            No follow-ups due today
                          </TableCell>
                        </TableRow>
                      ) : (
                        followupsDueToday.map((followup) => (
                          <TableRow
                            key={followup.id}
                            className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                          >
                            <TableCell className="px-6 py-4">
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                                  {followup.time}
                                </span>
                                {followup.status && (
                                  <Chip
                                    label={followup.status}
                                    size="small"
                                    className="bg-orange-100 text-orange-700 text-xs mt-1 w-fit"
                                  />
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="px-6 py-4">
                              <div className="text-sm font-bold text-slate-900 dark:text-white truncate max-w-[200px]">
                                {followup.name}
                              </div>
                            </TableCell>
                            <TableCell className="px-6 py-4">
                              <Chip
                                label={followup.currentStage || 'N/A'}
                                size="small"
                                className="bg-blue-100 text-blue-700 text-xs"
                              />
                            </TableCell>
                            <TableCell className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 truncate max-w-[200px]" title={followup.description}>
                              {followup.description}
                            </TableCell>
                            <TableCell className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="text"
                                  onClick={() => openWhatsApp(followup.phone, followup.whatsapp)}
                                  disabled={!followup.phone && !followup.whatsapp}
                                  sx={{
                                    color: '#25D366',
                                    minWidth: 'auto',
                                    padding: '4px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                    },
                                    '&:disabled': {
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  <FaWhatsapp size={20} />
                                </Button>
                                <Button
                                  variant="text"
                                  onClick={() => startPhoneCall(followup.phone)}
                                  disabled={!followup.phone}
                                  sx={{
                                    color: '#0284c7',
                                    minWidth: 'auto',
                                    padding: '4px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(2, 132, 199, 0.1)',
                                    },
                                    '&:disabled': {
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  <FiPhone size={20} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Today's Appointments */}
            <Card>
              <CardContent className="p-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  Today's Appointments
                </h3>
                <div className="max-h-[400px] overflow-y-auto">
                  <Stack spacing={3}>
                  {appointments.length === 0 ? (
                    <div className="text-center text-slate-500 py-8">
                      No appointments scheduled for today
                    </div>
                  ) : (
                    appointments.map((appointment, index) => (
                      <div key={appointment.id} className="relative">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <div className="flex flex-col items-center">
                              <div className="p-2 bg-gray-100 rounded-lg">
                                {getIconForType(appointment.type)}
                              </div>
                              {index < appointments.length - 1 && (
                                <div className="w-0.5 h-12 bg-gray-200 mt-2" />
                              )}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                                {appointment.time}
                              </span>
                              <Chip
                                label={appointment.tag}
                                size="small"
                                color={
                                  appointment.tagColor as 'success' | 'primary' | 'secondary'
                                }
                                className="text-xs"
                              />
                            </div>
                            <span className="text-sm font-bold text-slate-900 dark:text-white block mb-1">
                              {appointment.title}
                            </span>
                            <span className="text-xs text-slate-500 block mb-2">
                              {appointment.location}
                            </span>
                            {appointment.details && (
                              <span className="text-xs text-slate-600 dark:text-slate-400 block mb-2">
                                {appointment.details}
                              </span>
                            )}
                            <div className="flex gap-2 mt-2">
                              {appointment.type === 'site' && (
                                <>
                                  <Button size="small" variant="outlined" className="text-xs">
                                    Directions
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    className="bg-green-600 hover:bg-green-700 text-xs"
                                  >
                                    Start Visit
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  </Stack>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={isAddLeadModalOpen}
        onRequestClose={() => {
          setIsAddLeadModalOpen(false);
        }}
        onSuccess={() => {
          setIsAddLeadModalOpen(false);
        }}
      />
    </PageWrapper>
  );
}

export default DailySalesDashboard;


